import { type MetricNameValue } from '@canvas/frontend-metrics';
import { type Policy } from '@canvas/policies/types';

type CollaborationAbility = {
  /**
   * Whether a user can transmit this data to other users
   */
  canSend: boolean;
  /**
   * Whether a user can receive this data from other users
   */
  canConsume: boolean;
};

type AwarenessCapabilities = {
  /**
   * Selections in awareness refers to others being able to see the current users selections and vise-versa
   */
  selections: CollaborationAbility;
  /**
   * Telepointers in awareness refers to collaborator cursors being sent and received
   */
  telepointer: CollaborationAbility;
  /**
   * Transient awareness updates represent 'transient' data updates - e.g.
   * another user drags a sticky, which will eventually be persisted
   */
  transient: CollaborationAbility;
};

/**
 * Policies represent policy overrides we apply to our permissions
 * that we want to add for each mode
 */
type Policies = Array<Policy>;

/**
 * Update capabilities represent persistent document updates
 * Coming from other users and being sent from the current user
 */
type UpdateCapabilities = CollaborationAbility;

/**
 * Interface capabilities represent UI allowances or disallowances. E.g. in
 * specific modes, the main toolbar may not be visible.
 */
type InterfaceCapabilities = {
  /**
   * The primary toolbar in the middle bottom of the user's screen.
   */
  mainToolbar: { visible: boolean };
  /**
   * Navigation items provided from the inner frame to Confluence frontend.
   * Excluded for certain modes.
   */
  navigation: { visible: boolean };
  /**
   * Summary items rendered in the bottom left of the application. Used
   * currently by embedded mode to surface creation date of the whiteboard.
   */
  metadataFooter: { visible: boolean };
  /**
   * Displaying of zoom to selection in the bottom right menu.
   */
  zoomToSelection: { visible: boolean };
  /**
   * Tools which are not allowed in this version of the application.
   * NOTE: This should be powered by module-based APIs. For 'old' tools however,
   * this is an escape hatch provided to configure what is allowed or not. DO
   * NOT USE THIS OTHERWISE. Build on top of modules!
   */
  disallowedTools?: string[];
};

export type Capabilities = {
  key: string;
  observability: {
    /**
     * Performance metric key should be used for instrumenting load time for a
     * session in this mode.
     */
    load: MetricNameValue;
    /**
     * Tag to be associated with all analytics, Sentry errors, and server-side
     * requests in this mode. Not the same as {@link key}. Kept distinct for
     * backwards-compatibility with previous values for `tag` for existing modes.
     */
    tag: string;
  };
  awareness: AwarenessCapabilities;
  updates: UpdateCapabilities;
  policies: Policies;
  interface: InterfaceCapabilities;
};

export enum AvailableModes {
  /**
   * This is the normal mode, where everything is enabled
   */
  'readWrite' = 'readWrite',
  /**
   * This is the historical view mode, when looking at a historical version of a board
   */
  'historicalVersion' = 'historicalVersion',
  /**
   * Embedded mode for embedded Smart Link use cases
   */
  'embedded' = 'embedded',
}
